import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Trash2 } from "lucide-react";
import type { LayoutPreset } from "@/lib/layout-studio/types";
import { useLayoutTakeoverSchedules } from "@/hooks/useLayoutTakeoverSchedules";

interface LayoutTakeoverScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: LayoutPreset[];
  selectedPresetId: string | null;
}

export function LayoutTakeoverScheduleDialog({ open, onOpenChange, presets, selectedPresetId }: LayoutTakeoverScheduleDialogProps) {
  const { schedules, loading, saving, createSchedule, updateSchedule, deleteSchedule } = useLayoutTakeoverSchedules();

  const [name, setName] = useState("");
  const [presetId, setPresetId] = useState<string>(selectedPresetId || "");
  const [environment, setEnvironment] = useState<"staging" | "publish">("publish");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  const sorted = useMemo(() => [...schedules].sort((a, b) => a.starts_at.localeCompare(b.starts_at)), [schedules]);

  const handleCreate = async () => {
    if (!name.trim() || !presetId || !startsAt || !endsAt) return;
    await createSchedule({
      name: name.trim(),
      preset_id: presetId,
      environment,
      starts_at: new Date(startsAt).toISOString(),
      ends_at: new Date(endsAt).toISOString(),
      timezone: "America/Toronto",
      schedule_type: "ONE_TIME",
      is_enabled: true,
    });
    setName("");
    setStartsAt("");
    setEndsAt("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Schedule Takeover</DialogTitle>
          <DialogDescription>
            Automatically activate a layout preset at start time and auto-revert at end time.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="St. Patrick's Day takeover" />
          </div>
          <div className="space-y-2">
            <Label>Preset</Label>
            <Select value={presetId} onValueChange={setPresetId}>
              <SelectTrigger><SelectValue placeholder="Select preset" /></SelectTrigger>
              <SelectContent>
                {presets.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Environment</Label>
            <Select value={environment} onValueChange={(v) => setEnvironment(v as "staging" | "publish")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staging">Staging</SelectItem>
                <SelectItem value="publish">Live (Publish)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Starts</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Ends</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleCreate} disabled={saving || !presetId || !name || !startsAt || !endsAt}>Create Schedule</Button>
        </div>

        <Separator />

        <ScrollArea className="h-[280px]">
          <div className="space-y-2 pr-2">
            {loading && <div className="text-sm text-muted-foreground">Loading schedules...</div>}
            {!loading && sorted.length === 0 && <div className="text-sm text-muted-foreground">No schedules yet.</div>}
            {sorted.map((s) => {
              const presetName = presets.find((p) => p.id === s.preset_id)?.name || s.preset_id;
              return (
                <div key={s.id} className="border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{presetName}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={s.environment === "publish" ? "default" : "secondary"}>{s.environment}</Badge>
                      <Switch checked={s.is_enabled} onCheckedChange={(v) => updateSchedule(s.id, { is_enabled: v })} />
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteSchedule(s.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Start: {new Date(s.starts_at).toLocaleString()} ({formatDistanceToNow(new Date(s.starts_at), { addSuffix: true })})
                  </div>
                  <div className="text-xs text-muted-foreground">
                    End: {new Date(s.ends_at).toLocaleString()} ({formatDistanceToNow(new Date(s.ends_at), { addSuffix: true })})
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
